export type CustomerPhoneType = "Home" | "Cell";

export const PHONE_INPUT_MASK = "(###) ###-####";

export interface CustomerPhone {
  number?: string | number | null;
  type?: string | null;
  phoneType?: number | string | null;
  typeDescription?: string | null;
  isPreferred?: boolean | null;
}

export interface PhoneParts {
  areaCode: string;
  phonePrefix: string;
  phoneNumber: string;
}

const asCustomerPhone = (phone: unknown): CustomerPhone | null => {
  if (!phone || typeof phone !== "object") {
    return null;
  }

  return phone as CustomerPhone;
};

const asCustomerRecord = (customer: unknown): Record<string, unknown> | null => {
  if (!customer || typeof customer !== "object") {
    return null;
  }

  return customer as Record<string, unknown>;
};

export function normalizePhoneNumber(phoneNumber: unknown): string | null {
  if (typeof phoneNumber !== "string" && typeof phoneNumber !== "number") {
    return null;
  }

  let digits = String(phoneNumber).replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  return digits.length === 10 ? digits : null;
}

export function isPhoneNumberValid(phoneNumber: unknown): boolean {
  return normalizePhoneNumber(phoneNumber) !== null;
}

export function formatPhoneNumber(phoneNumber: unknown): string | null {
  const normalized = normalizePhoneNumber(phoneNumber);
  if (!normalized) {
    return null;
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

export function getPhoneHref(phoneNumber: unknown): string | null {
  const normalized = normalizePhoneNumber(phoneNumber);
  return normalized ? `tel:${normalized}` : null;
}

export function getPhoneParts(phoneNumber: unknown): PhoneParts | null {
  const normalized = normalizePhoneNumber(phoneNumber);
  if (!normalized) {
    return null;
  }

  return {
    areaCode: normalized.slice(0, 3),
    phonePrefix: normalized.slice(3, 6),
    phoneNumber: normalized.slice(6),
  };
}

export function getCustomerPhoneType(phone: unknown): CustomerPhoneType | null {
  const customerPhone = asCustomerPhone(phone);
  if (!customerPhone) {
    return null;
  }

  const type = [customerPhone.type, customerPhone.typeDescription]
    .find((value) => typeof value === "string" && value.trim())
    ?.replace(/[\s_-]+/g, "")
    .toLowerCase();

  if (type === "home" || type === "homephone") {
    return "Home";
  }

  if (
    type === "cell" ||
    type === "cellphone" ||
    type === "mobile" ||
    type === "mobilephone"
  ) {
    return "Cell";
  }

  const phoneTypeCode = String(customerPhone.phoneType ?? "").replace(/\D/g, "");
  if (phoneTypeCode === "72" || phoneTypeCode === "4") {
    return "Home";
  }

  if (phoneTypeCode === "67" || phoneTypeCode === "2") {
    return "Cell";
  }

  return null;
}

export function findCustomerPhone(
  phones: unknown,
  phoneType: CustomerPhoneType
): CustomerPhone | null {
  if (!Array.isArray(phones)) {
    return null;
  }

  let selectedPhone: CustomerPhone | null = null;

  for (const phone of phones) {
    const customerPhone = asCustomerPhone(phone);
    if (!customerPhone || getCustomerPhoneType(customerPhone) !== phoneType) {
      continue;
    }

    if (!selectedPhone) {
      selectedPhone = customerPhone;
      continue;
    }

    const phoneIsValid = normalizePhoneNumber(customerPhone.number) !== null;
    const selectedPhoneIsValid = normalizePhoneNumber(selectedPhone.number) !== null;
    const phoneIsPreferred = customerPhone.isPreferred === true;
    const selectedPhoneIsPreferred = selectedPhone.isPreferred === true;

    if (
      (phoneIsValid && !selectedPhoneIsValid) ||
      (phoneIsValid === selectedPhoneIsValid && phoneIsPreferred && !selectedPhoneIsPreferred)
    ) {
      selectedPhone = customerPhone;
    }
  }

  return selectedPhone;
}

export function getCustomerPhoneNumber(
  customer: unknown,
  phoneType: CustomerPhoneType,
): string | null {
  const customerData = asCustomerRecord(customer);
  if (!customerData) {
    return null;
  }

  const typedPhone = findCustomerPhone(customerData.phones, phoneType);
  if (isPhoneNumberValid(typedPhone?.number)) {
    return String(typedPhone?.number);
  }

  const legacyPhone = customerData[
    phoneType === "Home" ? "phone_Home" : "phone_Cell"
  ];
  return isPhoneNumberValid(legacyPhone) ? String(legacyPhone) : null;
}

export function mergeCustomerPhones(
  phones: unknown,
  managedPhones: CustomerPhone[],
): unknown[] {
  const existingPhones = Array.isArray(phones) ? phones : [];
  const preservedPhones = existingPhones.filter((phone) => {
    const customerPhoneType = getCustomerPhoneType(phone);
    return (
      phone &&
      customerPhoneType !== "Home" &&
      customerPhoneType !== "Cell"
    );
  });

  return [...preservedPhones, ...managedPhones];
}

export function getPreferredPhoneType(
  phones: unknown,
  customerPreferredPhone?: unknown,
): CustomerPhoneType | null {
  if (Array.isArray(phones)) {
    const preferredPhone = phones.find((phone) => {
      const customerPhone = asCustomerPhone(phone);
      return (
        customerPhone?.isPreferred === true &&
        getCustomerPhoneType(customerPhone) !== null
      );
    });

    const preferredPhoneType = getCustomerPhoneType(preferredPhone);
    if (preferredPhoneType) {
      return preferredPhoneType;
    }
  }

  const customerPreferred = asCustomerPhone(customerPreferredPhone);
  if (customerPreferred) {
    return getCustomerPhoneType(customerPreferred);
  }

  return null;
}

export function getCustomerPreferredPhoneType(
  customer: unknown,
): CustomerPhoneType | null {
  const customerData = asCustomerRecord(customer);
  if (!customerData) {
    return null;
  }

  const preferredPhoneType = getPreferredPhoneType(
    customerData.phones,
    customerData.preferredPhone,
  );
  if (preferredPhoneType) {
    return preferredPhoneType;
  }

  if (Array.isArray(customerData.phones) || customerData.preferredPhone) {
    return null;
  }

  const hasHomePhone = getCustomerPhoneNumber(customerData, "Home") !== null;
  const hasCellPhone = getCustomerPhoneNumber(customerData, "Cell") !== null;

  if (hasHomePhone !== hasCellPhone) {
    return hasHomePhone ? "Home" : "Cell";
  }

  return null;
}